<template>
	<tr class="hover:bg-primary/5 dark:hover:bg-primary-dark/5 transition-all duration-300 ease-out group">
		<td class="px-4 xl:px-6 py-4">
			<div class="flex items-center gap-3">
				<template v-if="shouldShowProfileImage">
					<img
						:src="profileImageUrl"
						:alt="`${displayName} profile photo`"
						class="h-10 w-10 xl:h-12 xl:w-12 rounded-full object-cover shadow-md flex-shrink-0 aspect-square"
						@error="onProfileImageError"
					/>
				</template>
				<div
				v-else
				class="h-10 w-10 xl:h-12 xl:w-12 rounded-full flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br flex-shrink-0"
				:class="avatarBgClass"
				aria-label="Default profile avatar"
				>
				{{ employeeInitials }}
				</div>
				<div class="min-w-0 flex-1">
					<div class="text-sm font-semibold text-slate-700 dark:text-slate-300 break-words">
						{{ displayName }}
					</div>
					<div class="text-sm text-slate-700 dark:text-slate-300 break-all">
						{{ employee.email }}
					</div>
				</div>
			</div>
		</td>
		<td class="hidden xl:table-cell px-4 xl:px-6 py-4 w-32">
			<div class="text-sm text-slate-700 dark:text-slate-300 truncate">
				{{ employee.phone || '—' }}
			</div>
		</td>
		<td class="px-4 xl:px-6 py-4 w-24">
			<StatusBadge :status="employee.status" />
		</td>
		<td class="hidden xl:table-cell px-4 xl:px-6 py-4 w-36">
			<div class="text-sm text-slate-700 dark:text-slate-300">
				{{ formatDate(employee.updated_at) }}
			</div>
		</td>
		<td class="px-4 xl:px-6 py-4 text-right w-40">
			<div class="flex items-center justify-center gap-1 xl:gap-2">
				<button @click="handleAction('view', employee.id)"
				class="p-1.5 xl:p-2 rounded-lg text-blue-600 dark:text-primary-dark/70 hover:text-primary dark:hover:text-primary-dark hover:bg-primary/10 dark:hover:bg-primary-dark/10 transition-all duration-200 hover:scale-110"
				title="View">
					<Eye class="h-4 w-4 xl:h-5 xl:w-5" />
				</button>
				<button v-if="employee.status === 'active'" @click="handleAction('deactivate', employee.id)"
				class="p-1.5 xl:p-2 rounded-lg text-warning/70 dark:text-warning-dark/70 hover:text-warning dark:hover:text-warning-dark hover:bg-warning/10 dark:hover:bg-warning-dark/10 transition-all duration-200 hover:scale-110"
				title="Deactivate">
					<Lock class="h-4 w-4 xl:h-5 xl:w-5" />
				</button>
				<button v-else-if="employee.status === 'inactive'" @click="handleAction('activate', employee.id)"
				class="p-1.5 xl:p-2 rounded-lg text-success/70 dark:text-success-dark/70 hover:text-success dark:hover:text-success-dark hover:bg-success/10 dark:hover:bg-success-dark/10 transition-all duration-200 hover:scale-110"
				title="Activate">
					<LockOpen class="h-4 w-4 xl:h-5 xl:w-5" />
				</button>
				<button v-else-if="employee.status === 'pending'" @click="handleAction('approve', employee.id)"
				class="p-1.5 xl:p-2 rounded-lg text-primary/70 dark:text-success-dark/70 hover:text-success dark:hover:text-success-dark hover:bg-success/10 dark:hover:bg-success-dark/10 transition-all duration-200 hover:scale-110"
				title="Approve">
					<UserRoundCheck class="h-4 w-4 xl:h-5 xl:w-5" />
				</button>
				<button v-if="employee.status === 'inactive' || employee.status === 'pending'" @click="handleAction('delete', employee.id)"
				class="p-1.5 xl:p-2 rounded-lg text-danger/70 dark:text-danger-dark/70 hover:text-danger dark:hover:text-danger-dark hover:bg-danger/10 dark:hover:bg-danger-dark/10 transition-all duration-200 hover:scale-110"
				title="Delete">
					<Trash class="h-4 w-4 xl:h-5 xl:w-5" />
				</button>
			</div>
		</td>
	</tr>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Eye, Lock, LockOpen, Trash, UserRoundCheck } from 'lucide-vue-next'
import api from '@/services/api'
import type { Employee } from '@/services/employeeService'
import StatusBadge from './StatusBadge.vue'

const props = defineProps<{
  	employee: Employee
}>()

const emit = defineEmits<{
	(e: 'view', id: number): void
	(e: 'activate', id: number): void
	(e: 'deactivate', id: number): void
	(e: 'approve', id: number): void
	(e: 'delete', id: number): void
}>()

const handleAction = (action: 'view' | 'activate' | 'deactivate' | 'approve' | 'delete', id: number) => {

	switch (action) {
		case 'view':
			emit('view', id)
		break
		case 'activate':
			emit('activate', id)
		break
		case 'approve':
			emit('approve', id)
		break
		case 'deactivate':
			emit('deactivate', id)
		break
		case 'delete':
			emit('delete', id)
		break
	}
}

const displayName = computed(() => {
	if (props.employee.first_name && props.employee.last_name) {
		return `${props.employee.first_name} ${props.employee.last_name}`
	}
	return props.employee.name
})

const employeeInitials = computed(() => {
	if (props.employee.first_name && props.employee.last_name) {
		return (props.employee.first_name[0] + props.employee.last_name[0]).toUpperCase()
	}

	const name = String(props.employee.name || '').trim()
	if (!name) return 'EM'

	const names = name.split(/\s+/).filter(Boolean)
	if (names.length >= 2) {
		return (names[0][0] + names[names.length - 1][0]).toUpperCase()
	}
	return name.substring(0, 2).toUpperCase()
})

const avatarLoadFailed = ref(false)

const profileImageUrl = computed(() => {
	const raw = String(props.employee.profile_picture || '').trim()
	if (!raw || raw.toLowerCase() === 'null') return ''

	if (/^https?:\/\//i.test(raw) || raw.startsWith('data:image/') || raw.startsWith('blob:')) {
		return raw
	}

	const rawBase = String(api.defaults.baseURL || '').replace(/\/$/, '')
	const baseNoApi = rawBase.replace(/\/api$/i, '')

	if (raw.startsWith('/storage/')) return `${baseNoApi}${raw}`
	if (raw.startsWith('storage/')) return `${baseNoApi}/${raw}`

	return `${baseNoApi}/storage/${raw.replace(/^\//, '')}`
})

const shouldShowProfileImage = computed(() => Boolean(profileImageUrl.value) && !avatarLoadFailed.value)

watch(profileImageUrl, () => {
  	avatarLoadFailed.value = false
})

const onProfileImageError = () => {
  avatarLoadFailed.value = true
}

const avatarBgClass = computed(() => {
	const gradients = [
		'from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover',
		'from-success to-success-hover dark:from-success-dark dark:to-success-dark-hover',
		'from-info to-info-hover dark:from-info-dark dark:to-info-dark-hover',
		'from-warning to-warning-hover dark:from-warning-dark dark:to-warning-dark-hover',
		'from-danger to-danger-hover dark:from-danger-dark dark:to-danger-dark-hover'
	]
	const index = props.employee.id % gradients.length
	return gradients[index]
})

const formatDate = (dateString: string): string => {
	if (!dateString) return '—'
	const date = new Date(dateString)

	// Fallback if date is invalid
	if (isNaN(date.getTime())) return '—'

	return date.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit'
	})
}
</script>
